import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useReducerStorage as useReducer, useToast } from '../hooks';
import { AuthenticatedUser } from '../models/authenticatedUser.models';
import { pushGTMEvent } from '../services';
import { useHttpClient } from './HttpContext';
import { useNetworkStatus } from './NetworkStatusContext';
import {
  authenticatedUserReducer,
  AuthenticatedUserState,
  initState,
} from './reducers/authenticatedUser.reducer';

type UpdatePayload = Pick<
  AuthenticatedUser,
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'title'
  | 'proposalPreferences'
  | 'notificationPreferences'
> & {
  smsOptInConsent?: boolean;
};

/** How long `fetchUser` waits for `/users/me` before failing the fetch. */
export const USER_FETCH_TIMEOUT_MS = 15_000;

interface AuthenticatedUserContextProps {
  user?: AuthenticatedUser;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchUser(options?: { signal?: AbortSignal }): Promise<void>;
  updateUser(user: UpdatePayload): Promise<void>;
  uploadAvatar(userId: string, file: File): Promise<void>;
}

const AuthenticatedUserContext = createContext<
  AuthenticatedUserContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AuthenticatedUserState>;
}>;

export const AuthenticatedUserProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    authenticatedUserReducer,
    {
      ...initState,
      ...initialState,
    },
    {
      key: 'airship-cache:authenticated-user',
      select: (s) => ({
        ids: s.ids,
        entities: s.entities,
        selectedId: s.selectedId,
      }),
    }
  );
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();

  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const user = useMemo(
    () => state.entities[state.selectedId || ''],
    [state.entities, state.selectedId]
  );

  const fetchUser: AuthenticatedUserContextProps['fetchUser'] = useCallback(
    async (options) => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      dispatch({ type: 'FETCH_USER' });

      const timeout = AbortSignal.timeout(USER_FETCH_TIMEOUT_MS);

      try {
        const user = await httpClient.get<AuthenticatedUser>(`/users/me`, {
          signal: options?.signal
            ? AbortSignal.any([options.signal, timeout])
            : timeout,
        });
        if (user.intercomUserHash) {
          pushGTMEvent('intercom_user_hash_ready', {
            intercom_user_hash: user.intercomUserHash,
          });
        }
        dispatch({
          type: 'FETCH_USER_SUCCESS',
          payload: { user },
        });
      } catch (error) {
        if (timeout.aborted) {
          dispatch({
            type: 'FETCH_USER_FAILURE',
            payload: { error: 'Timed out loading your account' },
          });
        } else if (error instanceof Error && error.message !== 'canceled') {
          dispatch({
            type: 'FETCH_USER_FAILURE',
            payload: { error: `${error}` },
          });
        }
      }
    },
    [httpClient, isOnline]
  );

  const updateUser: AuthenticatedUserContextProps['updateUser'] = useCallback(
    async (user) => {
      dispatch({ type: 'UPDATE_USER' });
      try {
        const updatedUser = await httpClient.put<AuthenticatedUser>(
          `/users/me`,
          user
        );
        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          payload: { user: updatedUser },
        });
      } catch (error) {
        dispatch({
          type: 'UPDATE_USER_FAILURE',
          payload: { error: `${error}` },
        });
      }
    },
    [httpClient]
  );

  const uploadAvatar: AuthenticatedUserContextProps['uploadAvatar'] =
    useCallback(
      async (userId, file) => {
        const metadataResponse = await httpClient.post<{
          preSignedUrl: string;
          filekeyId: string;
          fileKey: string;
        }>(`/users/${userId}/avatar`, {
          filename: file.name,
          contentType: file.type,
          uploadType: 'PROFILE_PHOTO',
        });
        await fetch(metadataResponse.preSignedUrl, {
          body: file,
          method: 'PUT',
        });
      },
      [httpClient]
    );

  return (
    <AuthenticatedUserContext.Provider
      value={{
        user,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        fetchUser,
        updateUser,
        uploadAvatar,
      }}
    >
      {children}
    </AuthenticatedUserContext.Provider>
  );
};

export const useAuthenticatedUser = () => {
  const context = useContext(AuthenticatedUserContext);
  if (context === undefined) {
    throw new Error(
      'useAuthenticatedUser must be used within an AuthenticatedUserProvider'
    );
  }

  return context;
};
